<template>
  <b-card class="metrics-container">
    <div class="metrics-grid">
      <template v-for="(metric, index) in localMetrics">
        <div v-if="index > 0" :key="'divider-' + index" class="divider"></div>
        <div :key="'metric-' + index" class="metric-section">
          <div class="metric-header">
            <div class="metric-icon">
              <i :class="metric.icon"></i>
            </div>
            <p class="metric-label">{{ metric.label }}</p>
          </div>
          <h3 class="metric-value">{{ metric.display }}</h3>
          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: metric.progress + '%' }"
            ></div>
          </div>
        </div>
      </template>
    </div>
  </b-card>
</template>

<script>
export default {
  name: "StatusMetrics",
  inject: {
    dashboardDisplayMode: {
      default: () => null,
    },
  },
  props: {
    statusData: {
      type: Array,
      required: true,
      default: () => [],
    },
    labels: {
      type: Object,
      default: () => ({
        total: "TOTAL DENÚNCIAS",
        new: "NOVAS",
        processing: "EM PROCESSAMENTO",
        concluded: "CONCLUÍDAS",
      }),
    },
  },
  computed: {
    displayMode() {
      return this.dashboardDisplayMode?.mode || "absolute";
    },
    totalValue() {
      return this.statusData.reduce((sum, item) => sum + item.count, 0);
    },
    newValue() {
      return this.statusData.find((item) => item._id === 0)?.count || 0;
    },
    processingValue() {
      return this.statusData.find((item) => item._id === 1)?.count || 0;
    },
    concludedValue() {
      return this.statusData.find((item) => item._id === 2)?.count || 0;
    },
    localMetrics() {
      const total = this.totalValue;
      const formatPct = (count) => {
        if (!total || total <= 0) return "0,00%";
        return `${((count / total) * 100).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`;
      };
      const isPercent = this.displayMode === "percent";
      return [
        {
          label: this.labels.total,
          value: total,
          display: total,
          icon: "fa fa-list",
          progress: total > 0 ? Math.min((total / 50) * 100, 100) : 0,
        },
        {
          label: this.labels.new,
          value: this.newValue,
          display: isPercent ? formatPct(this.newValue) : this.newValue,
          icon: "fa fa-plus",
          progress:
            total > 0 ? Math.min((this.newValue / total) * 100, 100) : 0,
        },
        {
          label: this.labels.processing,
          value: this.processingValue,
          display: isPercent
            ? formatPct(this.processingValue)
            : this.processingValue,
          icon: "fa fa-cog",
          progress:
            total > 0 ? Math.min((this.processingValue / total) * 100, 100) : 0,
        },
        {
          label: this.labels.concluded,
          value: this.concludedValue,
          display: isPercent
            ? formatPct(this.concludedValue)
            : this.concludedValue,
          icon: "fa fa-check",
          progress:
            total > 0 ? Math.min((this.concludedValue / total) * 100, 100) : 0,
        },
      ];
    },
  },
};
</script>

<style scoped>
.metrics-container {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
  margin-bottom: 2rem;
}

.metrics-grid {
  display: flex;
  align-items: stretch;
  min-height: 120px;
}

.metric-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 1.5rem 1rem;
  position: relative;
}

.divider {
  width: 1px;
  align-self: stretch;
  background: repeating-linear-gradient(
    to bottom,
    #cbd5e0 0px,
    #cbd5e0 6px,
    transparent 6px,
    transparent 12px
  );
  margin: 0;
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.metric-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #000000;
}

.metric-value {
  font-size: 2rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: #000000;
  line-height: 1;
}

.metric-label {
  font-size: 0.85rem;
  color: #000000;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.progress-container {
  width: 75%;
  height: 5px;
  background-color: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #4299e1;
  border-radius: 2px;
  transition: width 0.4s ease;
}

@media (max-width: 768px) {
  .metrics-grid {
    flex-direction: column;
    min-height: auto;
  }

  .divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .metric-section {
    padding: 1rem;
  }

  .metric-icon {
    width: 22px;
    height: 22px;
    font-size: 14px;
  }

  .metric-value {
    font-size: 1.75rem;
  }

  .metric-label {
    font-size: 0.8rem;
  }
}
</style>
